import { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FolderKanban,
  Sparkles,
  Quote,
  Settings,
  Inbox,
  ImagePlus,
  LogOut,
  Menu,
  X,
  ExternalLink,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/portfolio", label: "Portfolio", icon: FolderKanban },
  { to: "/admin/services", label: "Services", icon: Sparkles },
  { to: "/admin/testimonials", label: "Testimonials", icon: Quote },
  { to: "/admin/settings", label: "Site Settings", icon: Settings },
  { to: "/admin/messages", label: "Messages", icon: Inbox },
  { to: "/admin/media", label: "Media", icon: ImagePlus },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  function handleLogout() {
    logout();
    navigate("/admin/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-[#050507] text-white">
      {/* Mobile top bar */}
      <div className="flex items-center justify-between border-b border-white/[0.06] bg-[#050507]/90 px-4 py-3 lg:hidden">
        <Link to="/admin" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#0055ff] font-display text-base font-bold">
            P
          </div>
          <span className="font-display text-sm font-semibold tracking-tight">Parabolum CMS</span>
        </Link>
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-white/80"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${
            open ? "block" : "hidden"
          } fixed inset-0 z-50 w-72 border-r border-white/[0.06] bg-[#070709] lg:static lg:block lg:min-h-screen lg:w-72 lg:shrink-0`}
        >
          <div className="flex h-full flex-col">
            <div className="hidden items-center gap-3 border-b border-white/[0.06] px-6 py-5 lg:flex">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#0055ff] font-display text-lg font-bold">
                P
              </div>
              <div>
                <div className="font-display text-sm font-semibold tracking-tight">Parabolum</div>
                <div className="font-body text-[10px] tracking-[0.3em] uppercase text-white/40">CMS</div>
              </div>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-5">
              {NAV.map(({ to, end, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={() => setOpen(false)}
                  data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `mb-1 flex items-center gap-3 rounded-xl px-3 py-2.5 font-body text-sm transition ${
                      isActive
                        ? "bg-white/[0.07] text-white"
                        : "text-white/55 hover:bg-white/[0.04] hover:text-white"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </NavLink>
              ))}

              <div className="mt-6 border-t border-white/[0.06] pt-4">
                <a
                  href="/"
                  target="_blank"
                  rel="noreferrer"
                  className="mb-1 flex items-center gap-3 rounded-xl px-3 py-2.5 font-body text-sm text-white/55 transition hover:bg-white/[0.04] hover:text-white"
                >
                  <ExternalLink className="h-4 w-4" />
                  View website
                </a>
                <button
                  type="button"
                  onClick={handleLogout}
                  data-testid="admin-logout"
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 font-body text-sm text-white/55 transition hover:bg-red-500/10 hover:text-red-300"
                >
                  <LogOut className="h-4 w-4" />
                  Sign out
                </button>
              </div>
            </nav>

            <div className="border-t border-white/[0.06] px-5 py-4">
              <div className="truncate font-display text-sm text-white">{user?.name || "Admin"}</div>
              <div className="truncate font-body text-[11px] text-white/45">{user?.email}</div>
            </div>
          </div>
        </aside>

        {open && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setOpen(false)}
          />
        )}

        {/* Content */}
        <main className="min-h-screen flex-1 bg-[#050507]">
          <div key={location.pathname} className="animate-[fadein_0.4s_ease]">
            <Outlet />
          </div>
        </main>
      </div>

      <style>{`
        @keyframes fadein { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none } }
      `}</style>
    </div>
  );
}
